// linkHost.ts — 1.24.00 C1 fix round: the ONE place that decides what a link
// preview card names as its source.
//
// The source line is the member's only defence against a phishing link — it is
// the answer to "where does this actually go?". So it is derived from the URL
// and nothing else. A page's own `og:site_name` is attacker-controlled (any
// server can serve `<meta property="og:site_name" content="TruXSocial">`, or a
// member's bank) and it used to be exactly what the card printed. It never
// reaches the source line again.
//
// Deliberately NOT `new URL()`, on either surface: React Native's URL polyfill
// does not reliably expose `.host`, and a helper that behaves one way under
// jest (Node's URL) and another on a device is worse than no helper. The same
// regex and the same steps run on web and mobile, so a member who checks the
// same link on their phone and on the website is told the same host.
// mobile/src/core/lib/linkHost.ts is the hand-copied port, and
// linkHost.sync.test.ts there reads THIS file and fails when the two drift.
//
// Known limitation, stated rather than hidden: a unicode homograph host
// (Cyrillic "раypal.com") is shown as the device renders it. We never DECODE
// punycode — an `xn--…` host is printed verbatim, so a lookalike that arrives
// already-encoded stays visibly encoded — but neither surface ENCODES on the
// member's behalf either, because RN cannot, and one answer on two clients is
// worth more here than a better answer on one.

/**
 * The authority component of an http(s) URL.
 *
 * A backslash terminates it alongside `/`, `?` and `#`: for http(s) URLs the
 * WHATWG parser treats `\` as `/`, so `https://paypal.com\@evil.example/` really
 * does go to paypal.com and `@evil.example` is just path. Reading the backslash
 * as an ordinary character would name the wrong host — scarier than the truth,
 * but still a lie.
 */
export const AUTHORITY_RE = /^https?:\/\/([^/\\?#]*)/i;

/**
 * Longest host printed in full. Past this the LEADING labels are dropped, never
 * the trailing ones: `paypal.com.<70 more chars>.evil.example` must not be able
 * to push `evil.example` off the end of the line, because that is the only part
 * of the string that says where the link goes.
 */
export const MAX_HOST_CHARS = 40;

/** Drop leading labels (then, failing that, leading characters) until it fits. */
function elide(host: string): string {
  if (host.length <= MAX_HOST_CHARS) return host;
  const labels = host.split('.');
  for (let i = 1; i < labels.length; i++) {
    const tail = labels.slice(i).join('.');
    if (tail.length <= MAX_HOST_CHARS - 1) return `…${tail}`;
  }
  return `…${host.slice(host.length - (MAX_HOST_CHARS - 1))}`;
}

/**
 * The host to SHOW as a link's source: lower-cased, without userinfo, port,
 * a leading `www.`, a trailing root dot, or anything else that lets one string
 * read as a different domain than the one it resolves to.
 *
 * Anything that is not an http(s) URL comes back unchanged (still length-capped)
 * — a preview whose URL we cannot parse gets no invented host.
 */
export function hostForDisplay(url: string): string {
  // Tabs and newlines are stripped by every URL parser before it looks at the
  // string; leaving them in here would let one split the authority in two.
  const clean = url.replace(/[\t\n\r]/g, '').trim();
  const match = AUTHORITY_RE.exec(clean);
  if (!match?.[1]) return elide(clean);

  const authority = match[1];
  const host = authority
    // Everything up to the LAST '@' is userinfo, not a host: in
    // `https://paypal.com@evil.example/` the page served is evil.example's, and
    // that is the name the card has to print.
    .slice(authority.lastIndexOf('@') + 1)
    .replace(/:\d*$/, '')
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/^www\./, '');

  return host ? elide(host) : elide(clean);
}
